<template>
    <div class="muven-container">
        <div class="muven-router-name-container">
            <p class="muven-router-name">Categorias</p>
        </div>

        <div class="muven-content">
            <Loading page v-if="loading" />        

            <ConfirmationExclusionModalCmp 
                ref="confirmationExclusionModal"
                @confirm="confirmExclusionHandler" />

            <AuditListQuickVisualizationCmp 
                ref="auditListQuickVisualizationModal" />

            <div class="mb-3" v-show="!loading">
                <button class="btn btn-outline-secondary"
                    @click="gotoCategoryForm()">
                    Nova categoria
                </button>
            </div>

            <NothingFound v-if="nothingToShow" />

            <div v-if="!loading">
                <div class="muven-tip muven-tip-standalone tip-info mb-3" v-show="!nothingToShow">
                    <fa icon="info" class="tip-icon" />
                    <p>Apenas as categorias que tiverem pelo menos um produto vinculado serão enviadas para os canais.</p>
                </div>

                <div class="table-responsive"  v-show="!nothingToShow">
                    <table class="table table-sm table-hover table-striped">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr v-for="(cat, index) in categoryTree" :key="index">
                                <td :class="{
                                    'cat-depth-0': cat.depth === 0,
                                    'cat-depth-1': cat.depth === 1,
                                    'cat-depth-2': cat.depth === 2,
                                    'cat-depth-3': cat.depth === 3,
                                    'cat-depth-4': cat.depth === 4,
                                    'cat-depth-5': cat.depth === 5,
                                }" class="cat-depth-wtf">{{cat.name | infoOrPlaceholder}}</td>
                                <td>
                                    <span v-if="cat.isActive">
                                        <span class="muven-td-icon" >
                                            <fa icon="check-circle" 
                                                class="text-success" />
                                        </span>
                                    
                                        <span>Ativa</span>
                                    </span>

                                    <span v-if="!cat.isActive">
                                        <span class="muven-td-icon">
                                            <fa icon="ban"                                             
                                                class="text-muted" />
                                        </span>

                                        <span>Inativa</span>
                                    </span>
                                </td>
                                <td class="text-center muven-td-with-buttons">
                                    <button type="button" 
                                        class="btn btn-default text-muted" 
                                        data-toggle="dropdown" >
                                        <fa icon="ellipsis-v" v-show="!cat.loadingListAction" />
                                        <fa icon="sync-alt" spin v-show="cat.loadingListAction" />
                                    </button>

                                    <div class="dropdown-menu dropdown-menu-right muven-action-menu">
                                        <router-link class="dropdown-item"
                                            :to="detailUrl(cat)">
                                            <fa icon="file-alt" fixed-width class="muven-action-icon" />
                                            <span class="muven-action-label">Detalhes</span>
                                        </router-link>

                                        <span class="dropdown-item"
                                            @click="gotoCategoryForm(cat)">
                                            <fa icon="edit" fixed-width class="muven-action-icon" />
                                            <span class="muven-action-label">Editar</span>
                                        </span>

                                        <a class="dropdown-item"
                                            :href="getProductListFilterWithCategoryPath(cat)"
                                            target="_blank"
                                            rel="noopener"
                                            title="Abre em uma nova aba">
                                            <fa icon="box" fixed-width class="muven-action-icon" />
                                            <span class="muven-action-label">Visualizar produtos vinculados</span>
                                            <fa icon="external-link-alt" class="muven-action-external" />
                                        </a>

                                        <p class="dropdown-divider"></p>

                                        <span class="dropdown-item"
                                            @click="forceChannelsSync(cat)">
                                            <fa icon="sync-alt" fixed-width class="muven-action-icon" />
                                            <span class="muven-action-label">Forçar sincronização</span>
                                        </span>

                                        <p class="dropdown-divider"></p>

                                        <span class="dropdown-item muven-action-danger"
                                            @click="openConfirmationExclusionModal(cat)">
                                            <fa icon="trash-alt" fixed-width class="muven-action-icon" />
                                            <span class="muven-action-label">Excluir categoria</span>
                                        </span>

                                        <p class="dropdown-divider"></p>

                                        <span class="dropdown-item"
                                            @click="showAuditQuickVisualization(cat)">
                                            <fa icon="search" fixed-width class="muven-action-icon" />
                                            <span class="muven-action-label">Auditoria rápida</span>
                                        </span>

                                        <a class="dropdown-item"
                                            :href="getAuditUrl(cat.id, EntityEnum.Category)"
                                            target="_blank"
                                            rel="noopener"
                                            title="Abre em uma nova aba">
                                            <fa icon="history" fixed-width class="muven-action-icon" />
                                            <span class="muven-action-label">Ir para auditoria</span>
                                            <fa icon="external-link-alt" class="muven-action-external" />
                                        </a>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <ListCount :amount="categoryTree.length" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import categoryService from '@/category/services/CategoryService'
import modalService from '@/utils/services/ModalService'
import { dateTimeFilter } from '@/utils/filters/DateTimeFilter'
import { EntityEnum } from '@c2b/muven-commons'
import GetAuditUrl from '@/utils/mixins/GetAuditUrl'
import { infoOrPlaceholder } from '@/utils/filters/InformationOrPlaceholderFilter'
import ConfirmationExclusionModalCmp from '@/utils/components/ConfirmationExclusionModalCmp'
import { flattenTree } from '@/utils/normalizers/TreeNormalizer'
import AuditListQuickVisualizationCmp from '@/audit/components/AuditListQuickVisualizationCmp'

export default {
    name: 'CategoryListView',
    data() {
        return {
            EntityEnum,
            loading: true,
            categoryTree: [],
        }
    },
    mixins: [
        GetAuditUrl,
    ],
    filters: {
        dateTimeFilter,
        infoOrPlaceholder,
    },
    components: {
        ConfirmationExclusionModalCmp,
        AuditListQuickVisualizationCmp,
    },
    methods: {
        goto(category) {
            this.$router.push(`categorias/${category.id}`)
        },
        getProductListFilterWithCategoryPath(category) {
            return `/produtos?withCategory=${category.id}`
        },
        async showFilterModal() {
            await modalService.show(ID_FILTER_MODAL)
        },
        async closeFilterModal() {
            await modalService.hide(ID_FILTER_MODAL)
        },
        clearFilter() {
            this.categoryFilter = new CategoryListFilterModel()
        },
        openConfirmationExclusionModal(category) {
            const info = {
                id: category.id,
                title: 'Exclusão de categoria',
                message: `Tem certeza que deseja excluir a categoria <span style="font-weight: 600; color: var(--text-soft); text-transform: uppercase; letter-spacing: 1px;">${category.name}</span>?`,
            }

            this.$refs.confirmationExclusionModal.open(info)
        },
        async confirmExclusionHandler({id}) {
            try {
                await categoryService.deleteCategory(id)

                for (let i = 0; i < this.categoryTree.length; i++) {
                    if (this.categoryTree[i].id === id) {
                        this.$floatingAlert.success({
                            message: `Categoria <span style="font-weight: 600; color: var(--text-soft); text-transform: uppercase; letter-spacing: 1px;">${this.categoryTree[i].name}</span> excluída com sucesso.`,
                        })
                        this.categoryTree.splice(i, 1)
                        break
                    }
                }
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.$refs.confirmationExclusionModal.close()
        },
        gotoCategoryForm(category) {
            if (!category) {
                this.$router.push(`/categorias/nova`)
                return 
            }

            this.$router.push(`/categorias/${category.id}/edicao`)
        },
        async forceChannelsSync(cat) {
            cat.loadingListAction = true

            try {
                await categoryService.forceChannelsSync(cat.id)
                this.$floatingAlert.info({
                    message: `Solicitação de sincronização da categoria <b>${cat.name}</b> registrado com sucesso.`,
                    timeVisible: 10000,
                })
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message || `Houve um problema no momento de forçar a sincronização da categoria <b>${cat.name}</b>. Tente novamente mais tarde.`  
                })
            }

            cat.loadingListAction = false
        },
        async showAuditQuickVisualization(cat) {
            cat.loadingListAction = true

            await this.$refs.auditListQuickVisualizationModal.showModal({
                idEntity: cat.id,
                entityList: EntityEnum.Category,
                getDescription: () => `Categoria: ${cat.name}`,
            });

            cat.loadingListAction = false
        },
    },
    async mounted() {
        try {
            this.categoryTree = flattenTree(await categoryService.getCategoryTree())
        } catch (err) {
            this.$floatingAlert.error({
                message: err.message,
            })
        }

        this.loading = false
    },
    computed: {
        nothingToShow() {
            return !this.loading && !this.categoryTree.length
        },
        detailUrl() {
            return (cat) => {
                return `/categorias/${cat.id}`
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.table tbody > tr > td {
    &.cat-depth-0 {
        padding-left: 14px;
    }

    &.cat-depth-1 {
        padding-left: 54px;
    }

    &.cat-depth-2 {
        padding-left: 104px;
    }

    &.cat-depth-3 {
        padding-left: 154px;
    }

    &.cat-depth-4 {
        padding-left: 204px;
    }

    &.cat-depth-5 {
        padding-left: 254px;
    }
}
</style>
